<template>
  <div data-testid="signup-carousel">
    <div class="d-flex align-baseline mb-3">
      <i
        style="color: red"
        class="page-error"
        data-testid="signup-page-error"
        :key="pageErrorKey"
        v-show="pageError"
      >{{ pageError }}</i>
    </div>

    <!-- Show error state when subscription fails to load -->
    <div v-if="pageError && !accountSubscriptionInfo" class="text-center py-5">
      <v-icon size="64" color="grey" class="mb-3" icon="warning" />
      <h3 class="mb-3">Signup Temporarily Unavailable</h3>
      <p class="mb-4">{{ pageError }}</p>
      <v-btn color="primary" @click="location.reload()">Try Again</v-btn>
      <br><br>
      <a href="https://www.threadflow.com/pricing" target="_blank" class="text-decoration-none">
        <v-btn color="secondary" variant="outlined">View Pricing Information</v-btn>
      </a>
    </div>

    <!-- Normal signup flow when subscription loads successfully -->
    <template v-else-if="accountSubscriptionInfo">
      <div v-if="currentPage != 3" class="step-indicator">
        Step {{ currentPage + 1 }} of 3
      </div>
      <v-carousel
        :cycle="false"
        :show-arrows="false"
        v-model="currentPageLocal"
        hide-delimiters
        :continuous="false"
        light
        touchless
        :height="carouselHeight"
      >
        <!-- Step 1: Company Info -->
        <v-carousel-item :class="{ 'height-100': mobile }">
          <v-form v-model="companyFormValidLocal">
            <div class="d-flex flex-column justify-start">
              <h1 style="line-height: 25px" class="subheader">
                Let's set up your {{ title }} account!
              </h1>
              <p class="mb-4" style="font-size: 16px">
                Tell us about your company. This will be the name of your
                ThreadFlow profile
              </p>
              <v-text-field
                label="Company Name"
                data-testid="signup-company-name"
                v-model="distributor.name"
                :rules="[validateNotEmpty]"
                @keyup="$emit('change-company-name')"
                class="text-field"
              />
              <div class="d-flex text-field">
                <v-text-field
                  class="mr-2 pr-2"
                  label="Street and number *"
                  v-model="location.street_and_number"
                  :rules="[validateNotEmpty]"
                  data-testid="ct-locations-street"
                ></v-text-field>
                <v-text-field
                  label="Suite, Unit, Building, Floor, etc."
                  v-model="location.suite_unit_etc"
                  data-testid="ct-locations-suite"
                ></v-text-field>
              </div>
              <div class="d-flex text-field">
                <v-text-field
                  class="mr-2 pr-2"
                  label="City *"
                  v-model="location.city"
                  :rules="[validateNotEmpty]"
                  data-testid="ct-locations-city"
                ></v-text-field>
                <v-text-field
                  label="Postal Code *"
                  v-model="location.zip_code"
                  :rules="[validateNotEmpty]"
                  data-testid="ct-locations-zip"
                ></v-text-field>
              </div>
              <div class="d-flex text-field">
                <v-autocomplete
                  class="mr-2 pr-2"
                  hide-no-data
                  browser-autocomplete="disabled"
                  autocomplete="off"
                  label="State/Province *"
                  v-model="location.region"
                  :rules="[validateNotEmpty]"
                  data-testid="ct-locations-state"
                  :menu-props="{ contentProps: { 'data-testid': 'ct-locations-state-list' } }"
                  item-title="name"
                  item-value="code"
                  :items="usStates"
                ></v-autocomplete>
                <v-autocomplete
                  hide-no-data
                  browser-autocomplete="disabled"
                  label="Country *"
                  v-model="location.country"
                  :rules="[validateNotEmpty]"
                  data-testid="ct-locations-country"
                  item-title="name"
                  item-value="code"
                  :items="countriesAndCodes"
                ></v-autocomplete>
              </div>
            </div>
          </v-form>
        </v-carousel-item>

        <!-- Step 2: Account Credentials -->
        <v-carousel-item :class="{ 'height-100': mobile }">
          <v-form v-model="accountFormValidLocal">
            <div class="d-flex flex-column justify-start">
              <h1 class="subheader">Account Credentials</h1>
              <p class="mb-3" style="font-size: 16px">
                You will use this info to login to ThreadFlow
              </p>
              <v-text-field
                class="text-field"
                label="First Name"
                data-testid="signup-first-name"
                v-model="account.first_name"
                :rules="[validateNotEmpty]"
              />
              <v-text-field
                class="text-field"
                label="Last Name"
                data-testid="signup-last-name"
                v-model="account.last_name"
                :rules="[validateNotEmpty]"
              />
              <v-text-field
                class="text-field"
                label="Email Address"
                data-testid="signup-email"
                v-model="emailLocal"
                :rules="[validateNotEmpty, validateEmail]"
                @keyup="$emit('change-email')"
              />
              <v-text-field
                class="text-field"
                label="Password"
                data-testid="signup-password"
                type="password"
                v-model="account.password"
                :rules="passwordRules"
              />
              <v-text-field
                class="text-field"
                label="Phone number"
                data-testid="signup-phone"
                type="tel"
                autocomplete="tel"
                :maxlength="PHONE_MAX_LENGTH"
                hint="We use this for billing and account questions."
                persistent-hint
                v-model="account.phone"
                :rules="phoneRules"
              />
            </div>
          </v-form>
        </v-carousel-item>

        <!-- Step 3: Review and Sign Up -->
        <v-carousel-item :class="{ 'height-100': mobile }">
          <v-form v-model="checkoutFormValidLocal">
            <div class="d-flex flex-column justify-start" data-testid="signup-review">
              <h1 class="subheader">Review Your Order</h1>
              Review your company email and subscription details
              <v-text-field
                :class="mobile ? 'mt-2' : 'mt-3'"
                label="Email Address"
                v-model="emailLocal"
                :rules="[validateNotEmpty, validateEmail]"
                @keyup="$emit('change-email')"
              />
              <!-- Trial wording (OD-20261016-4; PR-D, design 1.5 D3): every plan,
                   whatever fees it carries (a flat monthly fee, per-store
                   fees, an allowance), takes NO payment at signup -- the
                   decided trial. The plan's fees are stated as post-trial
                   terms below, never as "Due Today". -->
              <div v-if="isTrial" data-testid="signup-trial-notice" :class="mobile ? 'mt-1' : 'mt-3'">
                <h2 :class="mobile ? 'subheader' : ''" :style="mobile ? '' : 'font-weight: normal'">
                  Free trial
                </h2>
                <div class="mr-2" data-testid="signup-plan-name">
                  Free trial: {{ accountSubscriptionInfo["name"] }}
                </div>
                <div v-if="planPriceLine" class="mt-1" data-testid="signup-plan-price">
                  {{ planPriceLine }}
                </div>
                <div v-if="trialDurationLabel" class="mt-2" data-testid="signup-trial-duration">{{ trialDurationLabel }}</div>
                <div class="mt-2">
                  No payment is due today, and no card is needed to sign up.
                  Your plan's pricing starts when your trial ends.
                </div>
                <div class="mt-2">We will email you ahead of the trial ending.</div>
                <div v-if="postTrialPricing" class="mt-2" data-testid="signup-trial-pricing">
                  {{ postTrialPricing }}
                </div>
              </div>
              <!-- Plan info not loaded: say nothing about money. -->
              <div v-else class="mt-3" data-testid="signup-pricing-loading">
                Loading pricing...
              </div>
              <div :class="mobile ? 'mt-1' : 'mt-4'" v-if="subscriptionID == '1'">
                <div class="d-flex align-center">
                  <v-text-field
                    label="Promo Code"
                    variant="outlined"
                    v-model="promoCodeLocal"
                    @keyup="$emit('update-promo-code')"
                    :style="mobile ? 'height: 55px' : ''"
                  >
                    <template v-slot:append>
                      <span>
                        <div
                          v-if="incorrectPromoCode"
                          class="incorrect-promo-code"
                        >
                          Incorrect
                        </div>
                        <div
                          v-else-if="!promoCodeValid"
                          @click="$emit('apply-promo-code')"
                          class="apply-promo-code-btn"
                        >
                          Apply
                        </div>
                        <div v-else class="valid-promo-code">Active</div>
                      </span>
                    </template>
                  </v-text-field>
                </div>
              </div>
              <v-spacer />
            </div>
          </v-form>
        </v-carousel-item>

        <!-- Post-submit: Congrats. Sized to its content (no spacers, no
             forced height): the card is only as tall as this text. -->
        <v-carousel-item>
          <div
            class="d-flex flex-column align-center text-center py-4"
            data-testid="signup-congrats"
          >
            <v-icon
              size="40"
              color="primary"
              class="mb-3"
              icon="mail_outline"
              data-testid="signup-congrats-icon"
            />
            <h1 class="subheader">Congrats! You're Almost There!</h1>
            <p data-testid="signup-congrats-email">
              We sent an activation link to
              <strong>{{ email }}</strong>.
            </p>
            <p class="mt-2" data-testid="signup-congrats-next">
              What happens next: activate your account, log in, then follow
              the setup checklist (Company, Payments, Suppliers, Compliance,
              your first store).
            </p>
            <div class="mt-4">
              <div>
                Didn't get your email?&nbsp;<a
                  class="sendEmailLink"
                  href="#"
                  data-testid="signup-congrats-resend"
                  :aria-disabled="resendBusy ? 'true' : 'false'"
                  @click.prevent="$emit('resend-email')"
                >Click here</a> to resend.
              </div>
              <!-- The real result of the last resend (card #670): set only
                   after the request settles, never optimistically. -->
              <p
                v-if="resendStatus"
                class="mt-2"
                :class="resendStatus.kind === 'sent' ? 'text-success' : 'text-error'"
                role="status"
                data-testid="signup-congrats-resend-status"
                :data-kind="resendStatus.kind"
              >{{ resendStatus.message }}</p>
              <div class="mt-2">Still aren't getting your email?</div>
              <div>
                Contact us at
                <span class="sendEmailLink">support@threadflow.com</span>
              </div>
            </div>
          </div>
        </v-carousel-item>
      </v-carousel>
    </template>

    <!-- Loading state -->
    <div v-else class="text-center py-5">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-3">Loading subscription information...</p>
    </div>
  </div>
</template>

<script>
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import { US_JURISDICTIONS } from "@/data/usStates";
import { planPriceLine as buildPlanPriceLine } from "@/utils/planPrice";
import { isValidPhone, PHONE_INVALID_MESSAGE, PHONE_MAX_LENGTH } from "@/utils/phone";
// The shared account-password policy (twin of api/src/password_policy.py).
import { passwordRules } from "@/utils/passwordPolicy";

export default {
  name: "SignUpCarousel",


  mixins: [rules, helpers],

  props: {
    currentPage: { type: Number, default: null },
    pageError: { type: String, default: null },
    pageErrorKey: { type: Number, default: 0 },
    accountSubscriptionInfo: { type: Object, default: null },
    title: { type: String, default: "" },
    subscriptionID: { type: String, required: true },
    signUpSharedUsers: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    // Form data (two-way via v-model pattern)
    distributor: { type: Object, required: true },
    account: { type: Object, required: true },
    email: { type: String, default: null },
    location: { type: Object, required: true },
    countriesAndCodes: { type: Array, default: () => [] },
    // Form validity
    companyFormValid: { type: Boolean, default: false },
    accountFormValid: { type: Boolean, default: false },
    checkoutFormValid: { type: Boolean, default: false },
    // Promo
    promoCode: { type: String, default: null },
    promoCodeValid: { type: Boolean, default: false },
    incorrectPromoCode: { type: Boolean, default: false },
    totalDue: { type: Number, default: 0 },
    // Congrats step resend: { kind: "sent" | "error", message } or null.
    resendStatus: { type: Object, default: null },
    resendBusy: { type: Boolean, default: false },
  },

  emits: [
    "update:currentPage",
    "update:companyFormValid",
    "update:accountFormValid",
    "update:checkoutFormValid",
    "update:email",
    "update:promoCode",
    "change-company-name",
    "change-email",
    "resend-email",
    "update-promo-code",
    "apply-promo-code",
  ],

  computed: {
    // The review step is tall and the card must not clip it, but the
    // post-submit step is short: let the carousel size to the active step
    // there instead of holding the tall step's height.
    carouselHeight() {
      if (this.currentPage === 3) return "auto";
      return this.mobile ? "100%" : undefined;
    },
    PHONE_MAX_LENGTH() {
      return PHONE_MAX_LENGTH;
    },
    // The owner's password: 8+ characters and a number, an upper-case
    // letter or a symbol. The server enforces the same rule.
    passwordRules() {
      return passwordRules;
    },
    // Required (Jake, 2026-10-19), checked with the same rules the server
    // applies (utils/phone.js mirrors api/src/utils/phone.py).
    phoneRules() {
      return [
        (v) => !!String(v ?? "").trim() || "Enter your phone number.",
        (v) => isValidPhone(v) || PHONE_INVALID_MESSAGE,
      ];
    },
    // The plan info must have LOADED before any claim about money is made: a
    // failed or slow getSubscriptionInfo must never read as "no payment is
    // due". Once loaded, EVERY plan is a trial with no payment at signup:
    // the plan's flat fee (unit_amount) is a post-trial term, not a charge
    // due today (design 1.5 D3).
    isTrial() {
      return !!this.accountSubscriptionInfo;
    },
    // The plan's configured trial length (pricing_plan.trial_days, from
    // getSubscriptionInfo). Empty when absent or 0: no guessing, no "0-day".
    trialDurationLabel() {
      const days = (this.accountSubscriptionInfo || {}).trial_days;
      return Number.isInteger(days) && days > 0 ? `${days}-day free trial` : "";
    },
    // The plan's headline price (flat monthly fee and/or per-active-store
    // price), from the shared helper so it reads like /pricing and the plan
    // picker. unit_amount is dollars (float); convert to integer cents.
    planPriceLine() {
      const info = this.accountSubscriptionInfo || {};
      return buildPlanPriceLine({
        monthlyCents:
          info.unit_amount != null ? Math.round(info.unit_amount * 100) : null,
        perActiveCents: info.per_store_full_cents,
      });
    },
    // The post-trial pricing line. A plan whose fees are only per-store keeps
    // the approved signup wording built from the plan's own per-store amounts
    // (cents, from getSubscriptionInfo). Every other shape (a flat fee, an
    // allowance, any combination) comes from the plan's `fee_terms`, the
    // server's one renderer (the same text the trial reminder email and the
    // Payments page state): the approved sentence when the shape has one,
    // else the fee list. Empty when nothing clear can be stated.
    postTrialPricing() {
      const info = this.accountSubscriptionInfo || {};
      const terms = info.fee_terms;
      if (terms && terms.shape !== "per_store") {
        return terms.sentence || `After your trial: ${terms.list_text}`;
      }
      const money = (cents) => `$${this.formatPrice(cents / 100)}`;
      const parts = [];
      if (info.per_store_full_cents != null) {
        parts.push(`${money(info.per_store_full_cents)} per active store per month`);
      }
      if (info.per_store_dormant_cents != null) {
        parts.push(
          `${money(info.per_store_dormant_cents)} per month for each inactive store (a store that is not live that month: a draft, inactive or closed store; archived stores are not charged)`
        );
      }
      return parts.length ? `After your trial: ${parts.join("; ")}.` : "";
    },
    currentPageLocal: {
      get() { return this.currentPage; },
      set(val) { this.$emit("update:currentPage", val); },
    },
    companyFormValidLocal: {
      get() { return this.companyFormValid; },
      set(val) { this.$emit("update:companyFormValid", val); },
    },
    accountFormValidLocal: {
      get() { return this.accountFormValid; },
      set(val) { this.$emit("update:accountFormValid", val); },
    },
    checkoutFormValidLocal: {
      get() { return this.checkoutFormValid; },
      set(val) { this.$emit("update:checkoutFormValid", val); },
    },
    emailLocal: {
      get() { return this.email; },
      set(val) { this.$emit("update:email", val); },
    },
    promoCodeLocal: {
      get() { return this.promoCode; },
      set(val) { this.$emit("update:promoCode", val); },
    },
    // US states/DC for the address step. item-value="code" so the field
    // stores the 2-letter abbreviation into location.region -- the same
    // value a typed entry produced and what the downstream address
    // validation + nexus_state consumers in SignUpModule expect.
    usStates() {
      // Alphabetical by name for the dropdown. US_JURISDICTIONS is in Census
      // region order (load-bearing for the nexus editor), so sort a copy.
      return [...US_JURISDICTIONS].sort((a, b) => a.name.localeCompare(b.name));
    },
  },

  methods: {
    formatPrice(number) {
      return number ? this.numberWithCommas(number.toFixed(2)) : "0.00";
    },
  },
};
</script>

<style scoped>
.page-error {
  position: absolute;
  z-index: 1;
  left: 0px;
  top: 0px;
  margin: 16px;
  margin-left: 30px;
}

.subheader {
  margin-bottom: 15px;
  font-weight: normal;
  color: #212121;
}

.text-field {
  /* Fixed spacing (not vh) so all four step-1 field rows plus the Next
     button fit without scrolling at a typical laptop card height. 4vh was
     viewport-height-relative and pushed State/Province + Country below the
     fold. */
  margin-bottom: 16px;
}

.step-indicator {
  margin-bottom: 12px;
}

p {
  color: #424242;
}

.sendEmailLink {
  font-weight: 600;
}

.height-100 {
  height: 100%;
}

.v-window__container {
  height: 100%;
}

.apply-promo-code-btn {
  color: #626262;
  margin-top: 2px;
  margin-right: 6px;
  margin-left: 12px;
  cursor: pointer;
}

.valid-promo-code {
  color: rgb(28, 204, 28);
  margin-top: 2px;
  margin-right: 6px;
  margin-left: 12px;
}

.incorrect-promo-code {
  color: #ff2e2e;
  margin-top: 2px;
  margin-right: 6px;
  margin-left: 12px;
}

.stripe-card-element {
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.42);
  border-radius: 4px;
  background: white;
  transition: border-color 0.2s;
}

.stripe-card-element:hover {
  border-color: rgba(0, 0, 0, 0.87);
}

.stripe-card-element.StripeElement--focus {
  border-color: #1976d2;
  border-width: 2px;
  padding: 11px;
}

.stripe-card-errors {
  color: #fa755a;
  font-size: 12px;
  min-height: 20px;
}
</style>
